/**
 * 모빌리티 허브 **제조사** 축 — 고정 목록 + 상품명 접두 판별. 순수 모듈.
 *
 * 왜 `specifications.maker` 가 아니라 이름인가: Bestbike 적재분은 상품명이 늘 제조사로
 * 시작하지만(`Honda Today …`, `Suzuki グラディウス …`), 제원의 maker 는 Benelli
 * (`レオンチーノ250 …`)가 "Other" 로 들어가 있다. 원문 이름(`products.name`)이 가장
 * 믿을 만하고, 없으면 제원 maker → 현지화 제목 순으로 본다(번역 제목은 "혼다"·"本田"처럼
 * 제조사가 번역돼 있어 언어별 별칭으로 맞춘다).
 *
 * 목록은 DB 가 아니라 이 파일의 상수다(순서 = 상품 수 내림차순). 라벨은 모든 언어에서
 * 라틴 표기 — 브랜드는 그 표기로 알아본다.
 */

export const BIKE_MAKER_KEYS = [
  "honda",
  "suzuki",
  "yamaha",
  "kawasaki",
  "bmw",
  "ktm",
  "benelli",
] as const;
export type BikeMaker = (typeof BIKE_MAKER_KEYS)[number];

/** 표시명 — 5개 언어 공통 라틴 표기. */
export const BIKE_MAKER_LABELS: Readonly<Record<BikeMaker, string>> = {
  honda: "Honda",
  suzuki: "Suzuki",
  yamaha: "Yamaha",
  kawasaki: "Kawasaki",
  bmw: "BMW",
  ktm: "KTM",
  benelli: "Benelli",
};

/**
 * 이름 앞머리 별칭(소문자·NFKC). 5개 언어의 번역 표기 + Benelli 는 모델명(레온치노)까지 —
 * Bestbike 원문이 제조사 없이 모델명으로 시작한다.
 */
export const BIKE_MAKER_ALIASES: Readonly<Record<BikeMaker, readonly string[]>> = {
  honda: ["honda", "ホンダ", "혼다", "本田"],
  suzuki: ["suzuki", "スズキ", "스즈키", "铃木", "鈴木"],
  yamaha: ["yamaha", "ヤマハ", "야마하", "雅马哈", "山葉"],
  kawasaki: ["kawasaki", "カワサキ", "가와사키", "川崎"],
  bmw: ["bmw"],
  ktm: ["ktm"],
  benelli: [
    "benelli",
    "ベネリ",
    "베넬리",
    "贝纳利",
    "貝納利",
    "leoncino",
    "レオンチーノ",
    "레온치노",
  ],
};

const MAKER_SET = new Set<string>(BIKE_MAKER_KEYS);

/** 긴 별칭부터 — 짧은 별칭이 긴 것의 앞부분일 때 잘못 먹지 않게. */
const ALIAS_TABLE: ReadonlyArray<readonly [string, BikeMaker]> = BIKE_MAKER_KEYS.flatMap(
  (maker) => BIKE_MAKER_ALIASES[maker].map((alias) => [alias, maker] as const),
).sort((a, b) => b[0].length - a[0].length);

function normalize(value: string): string {
  return value.normalize("NFKC").trim().toLowerCase();
}

/**
 * 한 문자열의 앞머리가 어느 제조사인가. 라틴 별칭은 바로 뒤가 라틴 글자면 다른 단어로
 * 본다("Hondaxx" ✕) — 숫자·공백·기호·끝은 통과("Leoncino250" ○, "BMW R1250" ○).
 */
export function makerFromText(value: string | null | undefined): BikeMaker | null {
  if (!value) return null;
  const text = normalize(value);
  if (!text) return null;
  for (const [alias, maker] of ALIAS_TABLE) {
    if (!text.startsWith(alias)) continue;
    const next = text.charAt(alias.length);
    if (/^[a-z]/.test(alias) && /[a-z]/.test(next)) continue;
    return maker;
  }
  return null;
}

/**
 * 후보를 **순서대로** 보고 처음 맞는 제조사. 서버는 (원문 이름, 제원 maker, 현지화 제목),
 * 클라이언트 폴백은 (제원 maker, 현지화 제목) 순으로 넘긴다. 전부 안 맞으면 null
 * (제조사 축이 켜지면 빠진다).
 */
export function resolveBikeMaker(
  ...candidates: ReadonlyArray<string | null | undefined>
): BikeMaker | null {
  for (const candidate of candidates) {
    const maker = makerFromText(candidate);
    if (maker) return maker;
  }
  return null;
}

/** URL 조각 → 알려진 제조사 키(대소문자 무시). 모르면 null. */
export function parseBikeMaker(value: unknown): BikeMaker | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  return MAKER_SET.has(key) ? (key as BikeMaker) : null;
}

export function bikeMakerLabel(key: BikeMaker): string {
  return BIKE_MAKER_LABELS[key];
}
